import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import ImgHeaderTitleEn from './images/title-en.webp'
import ImgHeaderTitleAr from './images/title-ar.webp'
import ImgHeart from './images/heart.webp'
import TabButtons from '@/components/TabButtons';
import Rewards from './components/Rewards';
import Rule from './components/Rule';
import Rank from './components/Rank';
import { getRank, getRewardsPool } from './services'
import { toast } from '@/utils/bridge'
import { useDesign } from '@/hooks'
import Loading from '@/components/Loading'
import Star from './components/Star'
import PaneTitle from './components/PaneTitle'
import './index.scss'

const { prefixCls } = useDesign('pg-week-star')

function getStars(data: WeekStar.RankItem[]) {
    return [
        {
            giftUrl: data[0] && data[0].giftUrl,
            item: data[0] && data[0].rankList && data[0].rankList[0]
        },
        {
            giftUrl: data[1] && data[1].giftUrl,
            item: data[1] && data[1].rankList && data[1].rankList[0]
        },
        {
            giftUrl: data[2] && data[2].giftUrl,
            item: data[2] && data[2].rankList && data[2].rankList[0]
        }
    ].filter(d => d.item && d.giftUrl)
}

function WeekStar() {
    const { t, i18n } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [state, setState] = useState<{
        selectedTab: number
        rankList: {
            thisWeek: WeekStar.RankItem[]
            lastWeek: WeekStar.RankItem[]
        },
        stars: {
            giftUrl: string
            item: WeekStar.SubRankItem
        }[]
        rewardsPool?: WeekStar.RewardsPool
    }>({
        selectedTab: 0,
        rankList: {
            thisWeek: [],
            lastWeek: []
        },
        stars: []
    })
    const refState = useRef(state)
    refState.current = state

    const tabsTitles = useMemo(() => {
        return ['WeekStar.ThisWeek', 'WeekStar.LastWeek', 'WeekStar.RewardRule']
    }, [])

    const seconds = useMemo(() => {
        return state.rewardsPool?.endTime ? Number(((state.rewardsPool.endTime - Date.now()) / 1000).toFixed(0)) : 0
    }, [state.rewardsPool?.endTime])

    const fetchGetRewardsPool = useCallback(async () => {
        const res = await getRewardsPool()
        if (!res) return
        if (res.code !== 200) {
            toast(res.message)
            return
        }

        setState({
            ...refState.current,
            rewardsPool: res.data || {},
        })
    }, [])

    const fetchRankList = useCallback(async () => {
        setLoading(true)
        const [resThis, resLast] = await Promise.all([
            getRank(1),
            getRank(0)
        ])
        setLoading(false)

        if (!resThis || !resLast) return

        if (resThis.code !== 200) {
            toast(resThis.message)
            return
        }

        if (resLast.code !== 200) {
            toast(resLast.message)
            return
        }


        setState({
            ...refState.current,
            rankList: {
                thisWeek: resThis.data,
                lastWeek: resLast.data
            },
            stars: getStars(resThis.data)
        })
    }, [])

    const handleTabChange = useCallback((i: number) => {
        const data = i == 1 ? refState.current.rankList.lastWeek : refState.current.rankList.thisWeek
        setState({
            ...refState.current,
            selectedTab: i,
            stars: getStars(data)
        })
    }, [])

    useEffect(() => {
        fetchRankList()
        fetchGetRewardsPool()
    }, [])

    return (
        <div className={clsx(prefixCls)}>
            {loading && <Loading />}
            <header className={`${prefixCls}__header`}>
                <img
                    className={`${prefixCls}__header-title`}
                    src={i18n.language === 'ar' ? ImgHeaderTitleAr : ImgHeaderTitleEn}
                />
                <img
                    className={`${prefixCls}__header-agency`}
                    src={ImgHeart}
                />
            </header>
            <div className={`${prefixCls}__stars`}>
                {state.stars.map((star, i) => <Star item={star.item} giftUrl={star.giftUrl} key={i} />)}
            </div>
            <TabButtons selectedTab={state.selectedTab} setSelectedTab={handleTabChange} tabText={tabsTitles}>
                {state.selectedTab === 0 &&
                    <div className={`${prefixCls}__container`}>
                        <PaneTitle title={t('WeekStar.CurrentRank')} />
                        <Rank
                            title="Current Rank"
                            selectedTab={state.selectedTab}
                            seconds={seconds}
                            rankList={state.rankList.thisWeek} />
                    </div>
                }
                {state.selectedTab === 1 &&
                    <div className={`${prefixCls}__container`}>
                        <PaneTitle title={t('WeekStar.LastWeekRank')} />
                        <Rank
                            title="Rank Rewards"
                            selectedTab={state.selectedTab}
                            rankList={state.rankList.lastWeek} />
                    </div>
                }
                {state.selectedTab === 2 &&
                    <div >
                        <div className={clsx(`${prefixCls}__container`)}>
                            <PaneTitle title={t('WeekStar.RankRewards')} />
                            <Rewards />
                        </div>
                        {state.rewardsPool && <div className={clsx(`${prefixCls}__container`)}>
                            <PaneTitle title={t('Common.ActiveRule')} />
                            <Rule activityThresholdValue={state.rewardsPool.activityThresholdValue} />
                        </div>}
                    </div>
                }
            </TabButtons>
        </div >
    )
}
export default WeekStar